import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageInput } from 'stream-chat-react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, DialogTitle, IconButton } from '@mui/material';
import Form from '@common/components/Form';
import { DateTime } from 'luxon';
import { Channel } from 'stream-chat/dist/types/channel';

import { sendScheduledMessage } from '@common/api/curators';
import CheckBoxField from '@common/components/Form/CheckBoxField';
import DateField from '@common/components/Form/DateField';
import TimeField from '@common/components/Form/TimeField';
import CustomChatInput from '@common/modules/Chat/CustomChatInput';

import { ActionsStyle, ContentStyle, HeaderStyle, ModalStyle, OverlayStyle } from './styled';

interface SendScheduledMessageModalProps {
	onClose: () => void;
	channel: Channel;
}

interface SimpleModalProps {
	title: string;
	onClose: () => void;
	children: React.ReactNode;
	actions?: React.ReactNode;
}

interface ScheduledMessageFormValues {
	scheduled_date: DateTime | null;
	scheduled_time: DateTime | null;
	duplicate_to_email: boolean;
}

const DEFAULT_TIME = DateTime.now().set({ hour: 8, minute: 0, second: 0, millisecond: 0 });

const validateForm = (values: ScheduledMessageFormValues) => {
	const errors: Partial<Record<keyof ScheduledMessageFormValues, string>> = {};

	if (!values.scheduled_date) {
		errors.scheduled_date = 'Select a date';
	}

	if (!values.scheduled_time) {
		errors.scheduled_time = 'Select a time';
	}

	return errors;
};

function SimpleModal({ title, onClose, children, actions }: SimpleModalProps) {
	const modal = (
		<>
			<OverlayStyle role="presentation" onClick={onClose} />
			<ModalStyle role="dialog" aria-modal="true" aria-label={title}>
				<HeaderStyle>
					<DialogTitle sx={{ padding: 0 }} variant="h5">
						{title}
					</DialogTitle>
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</HeaderStyle>
				<ContentStyle>{children}</ContentStyle>
				{actions && <ActionsStyle>{actions}</ActionsStyle>}
			</ModalStyle>
		</>
	);

	if (typeof document === 'undefined' || !document.body) {
		return modal;
	}

	return createPortal(modal, document.body);
}

export function buildStreamAttachments({
	imageUploads,
	fileUploads,
}: {
	imageUploads?: Record<string, any>;
	fileUploads?: Record<string, any>;
}) {
	const images = Object.values(imageUploads || {})
		.filter((u) => u.state === 'finished' && u.url)
		.map((u) => ({
			type: 'image',
			image_url: u.url,
			fallback: u.file?.name ?? 'image',
			original_width: u.original_width,
			original_height: u.original_height,
		}));

	const files = Object.values(fileUploads || {})
		.filter((u) => u.state === 'finished' && u.url)
		.map((u) => ({
			type: 'file',
			asset_url: u.url,
			title: u.file?.name,
			mime_type: u.file?.type,
			file_size: u.file?.size,
		}));

	return [...images, ...files];
}

function MassMailingModal({ onClose, channel }: SendScheduledMessageModalProps) {
	const [message, setMessage] = useState('');
	const [attachments, setAttachments] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const formRef = useRef<any>(null);

	const handleInputChange = useCallback(
		({ text, attachments }: { text: string; attachments: any[] }) => {
			setMessage(text);
			setAttachments(attachments);
		},
		[],
	);

	const ChatInput = useMemo(
		() =>
			// eslint-disable-next-line react/no-unstable-nested-components
			function ChatInput(props: any) {
				return <CustomChatInput {...props} hideSendButton onChange={handleInputChange} />;
			},
		[handleInputChange],
	);

	const handleSubmit = async (data: ScheduledMessageFormValues) => {
		if (!data.scheduled_date || !data.scheduled_time) return;

		const scheduledAt = data.scheduled_date
			.set({
				hour: data.scheduled_time.hour,
				minute: data.scheduled_time.minute,
				second: 0,
				millisecond: 0,
			})
			.toFormat('yyyy-MM-dd HH:mm:ss');

		setIsLoading(true);
		try {
			await sendScheduledMessage({
				channel_cid: channel.cid,
				scheduled_at: scheduledAt,
				duplicate_to_email: Boolean(data.duplicate_to_email),
				message,
				attachments,
			});
			onClose();
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<SimpleModal title="Scheduled Message" onClose={onClose}>
			<Form
				initialValues={{
					scheduled_date: DateTime.now().plus({ days: 1 }).startOf('day'),
					scheduled_time: DEFAULT_TIME,
					duplicate_to_email: false,
				}}
				validate={validateForm}
				loading={isLoading}
				onSave={handleSubmit}
				innerRef={formRef}
				actionButtonText="Confirm"
				formActionsStyles={{ display: 'flex', justifyContent: 'flex-end' }}
			>
				{({ setFieldValue }) => (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '6px',
							}}
						>
							<DateField
								name="scheduled_date"
								label="Date"
								onChange={(value: DateTime | null) => {
									setFieldValue('scheduled_date', value ? value.startOf('day') : null);
								}}
							/>
							<TimeField
								name="scheduled_time"
								label="Time"
								onChange={(value: DateTime | null) => {
									setFieldValue(
										'scheduled_time',
										value ? value.set({ second: 0, millisecond: 0 }) : null,
									);
								}}
							/>
							<CheckBoxField label="Duplicate to email" name="duplicate_to_email" />
						</Box>
						<MessageInput Input={ChatInput} />
					</Box>
				)}
			</Form>
		</SimpleModal>
	);
}

export default MassMailingModal;
